import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal as AntModal, Table, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { frenchDate } from "../../../variables/functions";
import AddExpert from "./AddExpert";

const EXPERTS_ENDPOINT = "/api/expertise/expert/all";
const PERSONNE_ENDPOINT = "/api/personne";

const buildExpertPayload = (values, current) => {
  const { telephone, ...rest } = values;
  const payload = {
    ...rest,
    fonction: "EXPERT",
    contact: telephone ? [telephone] : [],
  };

  if (current?.codeId) {
    payload.codeId = current.codeId;
  }

  return payload;
};

const Experts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);

  const fetchExperts = () => {
    setLoading(true);
    axiosInstance
      .get(EXPERTS_ENDPOINT)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const sorted = list.sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
        );
        setExperts(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des experts");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedExpert(null);
  };

  const handleSubmit = (values) => {
    const payload = buildExpertPayload(values, selectedExpert);
    setModalLoading(true);

    const request = selectedExpert
      ? axiosInstance.put(PERSONNE_ENDPOINT, JSON.stringify(payload))
      : axiosInstance.post(PERSONNE_ENDPOINT, JSON.stringify(payload));

    request
      .then(() => {
        toast.success(
          selectedExpert
            ? "Expert mis à jour avec succès"
            : "Expert ajouté avec succès"
        );
        handleCloseModal();
        fetchExperts();
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          selectedExpert
            ? "Erreur lors de la mise à jour de l'expert"
            : "Erreur lors de l'ajout de l'expert"
        );
      })
      .finally(() => setModalLoading(false));
  };

  const handleDelete = (expert) => {
    AntModal.confirm({
      title: "Supprimer cet expert ?",
      icon: <ExclamationCircleFilled style={{ color: "#fa541c" }} />,
      content: "Cette action est définitive",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        return axiosInstance
          .delete(`${PERSONNE_ENDPOINT}/${expert.codeId}`)
          .then(() => {
            toast.success("Expert supprimé avec succès");
            setExperts((prev) =>
              prev.filter((item) => item.codeId !== expert.codeId)
            );
          })
          .catch((err) => {
            console.error(err);
            toast.error("Erreur lors de la suppression de l'expert");
            throw err;
          });
      },
    });
  };

  const columns = [
    {
      title: "Nom & Prénom(s)",
      dataIndex: "nom",
      key: "nom",
      width: 180,
      render: (_, record) => {
        const fullName = `${record?.nom || ""} ${record?.prenom || ""}`.trim();
        return (
          <Tooltip title={fullName} placement="topLeft">
            <div style={{
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {fullName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "profession",
      key: "profession",
      width: 160,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <div style={{
            maxWidth: '160px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {value || "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Téléphone",
      key: "telephone",
      width: 130,
      render: (_, record) => (
        <div style={{
          maxWidth: '130px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {record?.contact?.[0] || "-"}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <div style={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {value || "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Adresse",
      dataIndex: "adresse",
      key: "adresse",
      width: 180,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <div style={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {value || "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Créé le",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (value) => (value ? <Tag>{frenchDate(value)}</Tag> : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => {
              setSelectedExpert(record);
              setModalVisible(true);
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined style={{ color: "#cf1322" }} />,
            label: <span style={{ color: "#cf1322" }}>Supprimer</span>,
            onClick: () => handleDelete(record),
          },
        ];

        return (
          <Dropdown
            menu={{
              items: items.map((item) => ({
                key: item.key,
                icon: item.icon,
                label: (
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick();
                    }}
                  >
                    {item.label}
                  </span>
                ),
              })),
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => {
                  setSelectedExpert(null);
                  setModalVisible(true);
                }}
              >
                Ajouter un expert
              </Button>
              <div className="card">
                <h5 className="card-header">Gestion des experts</h5>
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={experts}
                  rowKey={(record) => record.codeId}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 1200 }}
                />
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <AddExpert
        open={modalVisible}
        loading={modalLoading}
        expert={selectedExpert}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default Experts;